import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { ConnectionStatus, SignalRSensorData, SensorValue, UnitSensorMap, SignalRError } from '../types';
import { SLICE_NAME, DATA_KEY_SEPARATOR } from '../constants';

export interface SignalRLiveState {
	connectionStatus: ConnectionStatus;
	connectionId: string | null;
	lastError: SignalRError | null;
	sensorData: UnitSensorMap;
	subscribedSitesHash: string | null;
}

const initialState: SignalRLiveState = {
	connectionStatus: 'disconnected',
	connectionId: null,
	lastError: null,
	sensorData: {},
	subscribedSitesHash: null,
};

function createDataKey(dataType: string, measurement: string): string {
	return `${dataType}${DATA_KEY_SEPARATOR}${measurement}`;
}

function toSensorValue(data: SignalRSensorData): SensorValue {
	return {
		value: data.value,
		timestamp: data.dateTime,
		siteId: parseInt(data.siteId, 10),
		dataType: data.dataTypeName,
		measurement: data.measurement,
		isValid: data.isValueValid,
		depth: data.depth,
	};
}

export const signalRLiveSlice = createSlice({
	name: SLICE_NAME,
	initialState,
	reducers: {
		setConnectionStatus: (state, action: PayloadAction<ConnectionStatus>) => {
			state.connectionStatus = action.payload;
		},

		setConnectionId: (state, action: PayloadAction<string>) => {
			state.connectionId = action.payload;
			state.connectionStatus = 'connected';
		},

		setError: (state, action: PayloadAction<SignalRError>) => {
			state.lastError = action.payload;
			state.connectionStatus = 'error';
		},

		clearError: (state) => {
			state.lastError = null;
		},

		handleReconnect: (state) => {
			state.connectionStatus = 'reconnecting';
		},

		updateSensorData: (state, action: PayloadAction<SignalRSensorData[]>) => {
			for (const data of action.payload) {
				const unitId = parseInt(data.unitId, 10);
				const siteId = parseInt(data.siteId, 10);
				
				if (isNaN(unitId) || isNaN(siteId)) {
					continue;
				}

				if (!state.sensorData[unitId]) {
					state.sensorData[unitId] = {};
				}

				const dataKey = createDataKey(data.dataTypeName, data.measurement);

				state.sensorData[unitId][dataKey] = toSensorValue(data);
			}
		},

		resetConnection: (state) => {
			state.connectionStatus = 'disconnected';
			state.connectionId = null;
			state.lastError = null;
		},

		setSubscribedSites: (state, action: PayloadAction<string | null>) => {
			const previousSitesHash = state.subscribedSitesHash;
			state.subscribedSitesHash = action.payload;
			
			if (previousSitesHash && previousSitesHash !== action.payload) {
				state.sensorData = {};
			}
		},
	},
});

export const {
	setConnectionStatus,
	setConnectionId,
	setError,
	clearError,
	handleReconnect,
	updateSensorData,
	resetConnection,
	setSubscribedSites,
} = signalRLiveSlice.actions;

export const signalRLiveReducer = signalRLiveSlice.reducer;

